import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isCargoProblemDocumentMime, type CargoProblemMaterial } from '@magimp/shared';
import { Button, EditorModal, FileRow, type IconName } from '../../../ui/index.js';
import { getCargoProblemFileDownloadUrl } from '../../../api/cargo-problems.js';

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function isVideo(contentType: string): boolean {
  return contentType.startsWith('video/');
}

function isPdf(contentType: string): boolean {
  return contentType === 'application/pdf';
}

/**
 * WHY: the composer tiles describe a picked `File` while these tiles describe a
 * stored `CargoProblemMaterial`. Both read the same MIME/byte facts, so the
 * three formatters below take the raw values and are shared across the feature.
 */
export function cargoProblemFileIcon(contentType: string): IconName {
  if (isImage(contentType)) return 'image';
  if (isVideo(contentType)) return 'eye';
  if (isPdf(contentType) || isCargoProblemDocumentMime(contentType)) return 'file-text';
  return 'paperclip';
}

// The office MIME strings carry no readable subtype, so they are named
// explicitly; everything else in the allowlist is `image/png`-shaped.
const TYPE_LABELS: Record<string, string> = {
  'video/quicktime': 'MOV',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
};

/** Short human type of a file. Not translated: it names the format, not copy. */
export function cargoProblemFileType(contentType: string): string {
  return TYPE_LABELS[contentType] ?? (contentType.split('/')[1] ?? contentType).toUpperCase();
}

export function cargoProblemFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * WHY: a presigned URL is minted per open, not per render. Fetching one for
 * every attachment on every page would issue dozens of short-lived credentials
 * the reader never uses, so tiles stay typed metadata and the viewer resolves
 * the URL when the file is actually opened.
 */
export function CargoProblemAttachmentTiles({
  problemId,
  files,
}: {
  problemId: string;
  files: readonly CargoProblemMaterial[];
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  // WHY: a moderator deletion shrinks this list under an open viewer, so what
  // is on screen is remembered by file identity. A remembered POSITION would
  // point past the end of the shorter list and the viewer would read a file
  // that is no longer there; not finding the file closes the viewer instead.
  const openIndex = files.findIndex((file) => file.id === openId);
  if (files.length === 0) return null;
  return (
    <div className="cargo-problem-attachment-tiles">
      {files.map((file) => (
        <div key={file.id} className="cargo-problem-attachment-tile">
          <FileRow
            icon={cargoProblemFileIcon(file.contentType)}
            label={file.fileName}
            note={cargoProblemFileSize(file.contentLength)}
            onClick={() => setOpenId(file.id)}
          />
        </div>
      ))}
      {openIndex >= 0 ? (
        <CargoProblemAttachmentViewer
          problemId={problemId}
          files={files}
          index={openIndex}
          onIndexChange={(next) => setOpenId(files[next]?.id ?? null)}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </div>
  );
}

function CargoProblemAttachmentViewer({
  problemId,
  files,
  index,
  onIndexChange,
  onClose,
}: {
  problemId: string;
  files: readonly CargoProblemMaterial[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const file = files[index]!;
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setUrl(null);
    setFailed(false);
    getCargoProblemFileDownloadUrl(problemId, file.id)
      .then((response) => {
        if (active) setUrl(response.downloadUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [file.id, problemId]);

  return (
    <EditorModal
      open
      // WHY: the viewer only reads, so the shared unsaved-changes guard is
      // wired but permanently clean — closing never has anything to discard.
      dirty={false}
      onConfirmClose={onClose}
      title={file.fileName}
      width={880}
      footer={
        <>
          {files.length > 1 ? (
            <>
              <Button
                kind="ghost"
                icon="chevron-left"
                disabled={index === 0}
                onClick={() => onIndexChange(index - 1)}
              >
                {t('attachments.previous')}
              </Button>
              <Button
                kind="ghost"
                icon="chevron-right"
                disabled={index === files.length - 1}
                onClick={() => onIndexChange(index + 1)}
              >
                {t('attachments.next')}
              </Button>
            </>
          ) : null}
          <Button
            kind="primary"
            icon="download"
            disabled={!url}
            onClick={() => {
              if (url) window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            {t('attachments.download')}
          </Button>
        </>
      }
    >
      <div className="cargo-problem-attachment-preview">
        {failed ? <p role="alert">{t('attachments.previewError')}</p> : null}
        {!failed && url ? <Preview file={file} url={url} /> : null}
      </div>
    </EditorModal>
  );
}

function Preview({ file, url }: { file: CargoProblemMaterial; url: string }) {
  const { t } = useTranslation('cargoProblems');
  if (isImage(file.contentType)) return <img src={url} alt={file.fileName} />;
  if (isVideo(file.contentType)) {
    // WHY: no transcoding is in scope, so the browser plays the stored bytes
    // directly through the parent-scoped URL or shows its own codec fallback.
    // Uploaded evidence carries no caption track; the empty one satisfies the
    // a11y contract without claiming subtitles exist.
    return (
      <video src={url} controls aria-label={file.fileName}>
        <track kind="captions" />
      </video>
    );
  }
  if (isPdf(file.contentType)) return <iframe src={url} title={file.fileName} />;
  return <p>{t('attachments.noPreview')}</p>;
}
